import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Phone, MapPin, Clock, Mail, MessageCircle, Send, CheckCircle, Navigation, Car, Bus, Train } from 'lucide-react'
import { Header } from '@/components/Header'
import { Footer } from '@/components/Footer'
import '@/styles/animations.css'

export function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  })
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError('')
    
    try {
      const formDataToSend = new FormData()
      formDataToSend.append('access_key', import.meta.env.VITE_WEB3FORMS_ACCESS_KEY)
      formDataToSend.append('name', formData.name)
      formDataToSend.append('email', formData.email)
      formDataToSend.append('phone', formData.phone)
      formDataToSend.append('subject', formData.subject)
      formDataToSend.append('message', formData.message)
      formDataToSend.append('from_name', 'SSS Dental Contact Form')
      
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formDataToSend
      })
      
      const result = await response.json()
      
      if (result.success) {
        setIsSubmitted(true)
      } else {
        setSubmitError('Failed to send message. Please try again or use WhatsApp.')
      }
    } catch (error) {
      console.error('Form submission error:', error)
      setSubmitError('Network error. Please check your connection and try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const generateWhatsAppLink = () => {
    const message = `Hi! I have a query.\n\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nSubject: ${formData.subject}\nMessage: ${formData.message}`
    return `https://wa.me/919841817510?text=${encodeURIComponent(message)}`
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-lg text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Message Sent Successfully!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for contacting us. We'll get back to you within 24 hours.
          </p>
          <div className="space-y-4">
            <Button asChild className="w-full">
              <Link to="/">Return to Home</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <a href={generateWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 h-4 w-4" />
                Continue on WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <Header currentPage="contact" />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 via-yellow-500/8 to-yellow-600/5 animate-pulse"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-20 h-20 bg-gradient-to-br from-yellow-400/20 to-yellow-600/15 rounded-full blur-xl animate-bounce" style={{animationDelay: '0s'}}></div>
          <div className="absolute top-1/2 right-20 w-16 h-16 bg-gradient-to-br from-yellow-500/15 to-yellow-700/10 rounded-full blur-lg animate-bounce" style={{animationDelay: '2s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-fade-in-up" style={{animationDelay: '0.2s', animationFillMode: 'both'}}>
              Contact <span className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">Us</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto animate-fade-in-up" style={{animationDelay: '0.4s', animationFillMode: 'both'}}>
              Get in touch with us for appointments, queries, or emergency dental care. We're here to help with all your dental needs.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="text-center p-8 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Phone className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Call Us</h3>
              <p className="text-gray-600 mb-4">Speak directly with our team</p>
              <div className="space-y-2">
                <p className="text-lg font-medium text-gray-900">+91-9841817510</p>
                <p className="text-lg font-medium text-gray-900">+91-6381823239</p>
                <p className="text-sm text-gray-500">Open All Days - Because your smile can't wait</p>
                <p className="text-sm text-gray-500">9 AM - 8:30 PM</p>
              </div>
              <Button asChild className="mt-4">
                <a href="tel:+91-9841817510">Call Now</a>
              </Button>
            </div>

            <div className="text-center p-8 bg-green-50 rounded-lg">
              <div className="w-16 h-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <MessageCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">WhatsApp</h3>
              <p className="text-gray-600 mb-4">Quick and easy messaging</p>
              <div className="space-y-2">
                <p className="text-lg font-medium text-gray-900">+91-9841817510</p>
                <p className="text-lg font-medium text-gray-900">+91-6381823239</p>
                <p className="text-sm text-gray-500">Available 24/7</p>
              </div>
              <Button asChild className="mt-4 bg-green-600 hover:bg-green-700">
                <a href="https://wa.me/919841817510" target="_blank" rel="noopener noreferrer">
                  Message Us
                </a>
              </Button>
            </div>

            <div className="text-center p-8 bg-purple-50 rounded-lg">
              <div className="w-16 h-16 bg-purple-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Mail className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Email Us</h3>
              <p className="text-gray-600 mb-4">Send us your detailed queries</p>
              <div className="space-y-2">
                <p className="text-lg font-medium text-gray-900">smile@sssdental.com</p>
                <p className="text-sm text-gray-500">We'll respond within 24 hours</p>
              </div>
              <Button asChild className="mt-4 bg-purple-600 hover:bg-purple-700">
                <a href="mailto:smile@sssdental.com">Send Email</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section id="contact" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <input type="hidden" name="access_key" value={import.meta.env.VITE_WEB3FORMS_ACCESS_KEY} />
                <input type="hidden" name="from_name" value="SSS Dental Contact Form" />
                <input type="hidden" name="redirect" value="false" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="What is your query about?"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us more about your query or concern..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {submitError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600 text-sm">{submitError}</p>
                  </div>
                )}
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button type="submit" size="lg" className="flex-1" disabled={isSubmitting}>
                    <Send className="mr-2 h-5 w-5" />
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                  <Button asChild variant="outline" size="lg" className="flex-1">
                    <a href={generateWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="mr-2 h-5 w-5" />
                      Send via WhatsApp
                    </a>
                  </Button>
                </div>
              </form>
            </div>

            {/* Clinic Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Visit Our Clinic</h2>
              
              <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Sri Sai Sabari Dental Clinic</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <MapPin className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                    <div>
                      <p className="font-medium text-gray-900">Address</p>
                      <p className="text-gray-600">East Main Road, opposite to leobags</p>
                      <p className="text-gray-600">Puzhuthivakkam, Madipakkam</p>
                      <p className="text-gray-600">Chennai, Tamil Nadu 600091</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Phone className="h-6 w-6 text-blue-600 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">Phone</p>
                      <p className="text-gray-600">+91-9841817510 / +91-6381823239</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Mail className="h-6 w-6 text-blue-600 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">Email</p>
                      <p className="text-gray-600">smile@sssdental.com</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-6 w-6 text-blue-600 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">Hours</p>
                      <p className="text-gray-600">Open All Days - Because your smile can't wait</p>
                      <p className="text-gray-600">9:00 AM - 8:30 PM</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t">
                  <Button asChild className="w-full">
                    <a href="https://maps.app.goo.gl/QDpBdRjGZW2eKb2m7" target="_blank" rel="noopener noreferrer">
                      <Navigation className="mr-2 h-5 w-5" />
                      Get Directions
                    </a>
                  </Button>
                </div>
              </div>

              {/* How to Reach */}
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">How to Reach Us</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Car className="h-5 w-5 text-blue-600 mr-3" />
                    <span className="text-gray-600">By Car: Parking available in front of the clinic</span>
                  </div>
                  <div className="flex items-center">
                    <Bus className="h-5 w-5 text-blue-600 mr-3" />
                    <span className="text-gray-600">By Bus: Multiple bus routes available to Madipakkam</span>
                  </div>
                  <div className="flex items-center">
                    <Train className="h-5 w-5 text-blue-600 mr-3" />
                    <span className="text-gray-600">By Train: Nearest station is Tambaram (8 km away)</span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-yellow-50 rounded-md">
                  <p className="text-sm text-gray-600">
                    <strong>Landmark:</strong> Located opposite to Leo bags store on East Main Road
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Find Us on Map</h2>
            <p className="text-xl text-gray-600 mb-8">Easy to locate and accessible by all modes of transport</p>
            <div className="bg-blue-50 rounded-lg p-8">
              <MapPin className="h-16 w-16 text-blue-600 mx-auto mb-6" />
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Location</h3>
              <p className="text-gray-600 mb-6">East Main Road, opposite to leobags<br />Puzhuthivakkam, Madipakkam, Chennai</p>
              <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700">
                <a href="https://maps.app.goo.gl/QDpBdRjGZW2eKb2m7" target="_blank" rel="noopener noreferrer">
                  <MapPin className="mr-2 h-5 w-5" />
                  View on Google Maps
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="py-16 bg-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Dental Emergency?</h2>
            <p className="text-xl text-gray-600 mb-8">
              We provide immediate care for dental emergencies. Contact us right away!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <a href="tel:+91-9841817510">
                  <Phone className="mr-2 h-5 w-5" />
                  Emergency Call: +91-9841817510
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="https://wa.me/919841817510?text=I%20have%20a%20dental%20emergency" target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  WhatsApp Emergency
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}